/**
 * One application: applicant/unit detail, uploaded documents, screening
 * results (staff-entered manually — no vendor integration, see
 * modules/applications/service.ts's header), and the decision.
 *
 * `decideApplication` on the API runs the decision, the submittedAt-order
 * check, and — on a decline — the placeholder decision-notice generation
 * all in one transaction (invariant 8). This page just calls it and shows
 * the result; it does not need to know any of that happened, same as
 * WorkOrderShow not needing to know assigning a vendor sent an email.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useInvalidate, useList, useOne } from '@refinedev/core';
import { api } from '../../../lib/api.js';
import { humanizeEnum } from '../../../components/Form.js';
import { DocumentList } from '../../../components/DocumentList.js';
import { useCan } from '../../../lib/capabilities.js';

interface ApplicationDetail {
  id: string;
  status: string;
  submittedAt: string | null;
  applicantName: string;
  applicantEmail: string;
  applicantPhone: string | null;
  unitLabel: string;
  propertyName: string;
}

interface ApplicantSensitive {
  ssn: string | null;
  ssnLast4: string | null;
  legalAgeAttested: boolean;
  authorizationGivenAt: string | null;
}

interface ScreeningResult {
  id: string;
  vendorReference: string | null;
  reasonCodes: unknown;
  notes: string | null;
  enteredAt: string | null;
}

interface ApplicationDecision {
  decision: string;
  screeningCriterionId: string | null;
  decidedAt: string | null;
}

interface ScreeningCriterionOption {
  id: string;
  code: string;
  version: number;
  description: string;
}

interface DetailResponse {
  application: ApplicationDetail;
  decision: ApplicationDecision | null;
  screeningResults: ScreeningResult[];
}

export function ApplicationShow() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const canWrite = useCan('applications:write');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [vendorReference, setVendorReference] = useState('');
  const [screeningNotes, setScreeningNotes] = useState('');

  const [decisionKind, setDecisionKind] = useState<'APPROVED' | 'DECLINED'>('APPROVED');
  const [criterionId, setCriterionId] = useState('');

  const { data, isLoading } = useOne<DetailResponse>({
    resource: 'staff/applications',
    id: id ?? '',
    queryOptions: { enabled: Boolean(id) },
  });

  const { data: sensitiveData } = useOne<ApplicantSensitive>({
    resource: 'applications',
    id: id ? `${id}/sensitive` : '',
    queryOptions: { enabled: Boolean(id) },
  });

  const { data: criteriaData } = useList<ScreeningCriterionOption>({
    resource: 'staff/screening-criteria',
    pagination: { mode: 'off' },
  });

  async function refresh() {
    await invalidate({
      resource: 'staff/applications',
      invalidates: ['detail'],
      ...(id ? { id } : {}),
    });
  }

  async function run(action: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await action();
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That action failed.');
    } finally {
      setBusy(false);
    }
  }

  function recordScreeningResult() {
    void run(async () => {
      await api(`/staff/applications/${id ?? ''}/screening-results`, {
        method: 'POST',
        body: JSON.stringify({
          vendorReference: vendorReference || undefined,
          notes: screeningNotes || undefined,
        }),
      });
      setVendorReference('');
      setScreeningNotes('');
    });
  }

  function decide() {
    if (decisionKind === 'DECLINED' && !criterionId) {
      setError('Select a criterion to decline against.');
      return;
    }
    void run(() =>
      api(`/staff/applications/${id ?? ''}/decision`, {
        method: 'POST',
        body: JSON.stringify({
          decision: decisionKind,
          ...(decisionKind === 'DECLINED' ? { screeningCriterionId: criterionId } : {}),
        }),
      }),
    );
  }

  if (isLoading) return <p className="muted">Loading…</p>;
  const application = data?.data.application;
  const decision = data?.data.decision ?? null;
  const screeningResults = data?.data.screeningResults ?? [];
  const sensitive = sensitiveData?.data;
  const criteria = criteriaData?.data ?? [];

  if (!application) return <p className="alert alert--error">Application not found.</p>;

  return (
    <section className="stack">
      <div className="row">
        <h1>{application.applicantName}</h1>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/staff/applications')}
        >
          ← All applications
        </button>
      </div>

      {error ? <p className="alert alert--error">{error}</p> : null}

      <div className="card stack">
        <div className="row">
          <span className="pill">{humanizeEnum(application.status)}</span>
          <span className="muted">
            {application.unitLabel}, {application.propertyName}
          </span>
        </div>
        <p className="muted">
          {application.applicantEmail}
          {application.applicantPhone ? ` · ${application.applicantPhone}` : ''}
        </p>
        <p className="muted">
          {application.submittedAt
            ? `Submitted ${application.submittedAt.slice(0, 16).replace('T', ' ')}`
            : 'Not yet submitted'}
        </p>
      </div>

      <h2>Sensitive information</h2>
      <div className="card">
        {sensitive ? (
          <>
            <p>
              SSN: {sensitive.ssn ?? (sensitive.ssnLast4 ? `ending in ${sensitive.ssnLast4}` : '—')}
            </p>
            <p className="muted">
              Legal age attested: {sensitive.legalAgeAttested ? 'Yes' : 'No'} · Authorization:{' '}
              {sensitive.authorizationGivenAt
                ? sensitive.authorizationGivenAt.slice(0, 10)
                : 'Not given'}
            </p>
          </>
        ) : (
          <p className="muted">Nothing on file yet.</p>
        )}
      </div>

      <h2>Documents</h2>
      <DocumentList
        filter={{ applicationId: application.id }}
        emptyMessage="No documents uploaded yet."
      />

      <h2>Screening</h2>
      <p className="muted">
        Entered manually — no screening vendor is integrated yet (see CLAUDE.md's Applications
        section). Never the raw report, just the vendor reference and outcome.
      </p>
      {screeningResults.length === 0 ? (
        <p className="muted">No screening results recorded yet.</p>
      ) : (
        <ul className="stack">
          {screeningResults.map((result) => (
            <li key={result.id} className="card">
              <p>{result.vendorReference ?? 'No vendor reference'}</p>
              {result.notes ? <p className="muted">{result.notes}</p> : null}
              <p className="muted">{result.enteredAt?.slice(0, 16).replace('T', ' ')}</p>
            </li>
          ))}
        </ul>
      )}

      {canWrite && application.status === 'SUBMITTED' ? (
        <div className="card stack">
          <h3>Record a screening result</h3>
          <input
            className="field__input"
            placeholder="Vendor reference"
            value={vendorReference}
            onChange={(event) => setVendorReference(event.target.value)}
          />
          <input
            className="field__input"
            placeholder="Notes"
            value={screeningNotes}
            onChange={(event) => setScreeningNotes(event.target.value)}
          />
          <button
            type="button"
            className="button button--quiet"
            disabled={busy}
            onClick={recordScreeningResult}
          >
            Record result
          </button>
        </div>
      ) : null}

      <h2>Decision</h2>
      {decision ? (
        <div className="card">
          <p>
            <strong>{humanizeEnum(decision.decision)}</strong>
            {decision.decidedAt ? ` · ${decision.decidedAt.slice(0, 16).replace('T', ' ')}` : ''}
          </p>
        </div>
      ) : canWrite && application.status === 'SUBMITTED' ? (
        <div className="card stack">
          <div className="row">
            <select
              className="field__input"
              value={decisionKind}
              onChange={(event) => setDecisionKind(event.target.value as 'APPROVED' | 'DECLINED')}
            >
              <option value="APPROVED">Approve</option>
              <option value="DECLINED">Decline</option>
            </select>

            {decisionKind === 'DECLINED' ? (
              <select
                className="field__input"
                value={criterionId}
                onChange={(event) => setCriterionId(event.target.value)}
              >
                <option value="">Select a criterion…</option>
                {criteria.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} v{c.version}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
          <button type="button" className="button" disabled={busy} onClick={decide}>
            Record decision
          </button>
        </div>
      ) : (
        <p className="muted">No decision yet.</p>
      )}
    </section>
  );
}
